import ActiveDirectoryRoutes from 'client/directories/ActiveDirectory/ActiveDirectoryRoutes';
import ApplicationRoutes from 'client/applications/ApplicationRoutes';
import AuthNPoliciesRoutes from 'client/Security/Routes/AuthNPoliciesRoutes';
import CloudDirectoryRoutes from 'client/directories/CloudDirectoryRoutes';
import CloudInsightsRoutes from 'client/CloudInsights/CloudInsightsRoutes';
import CommandTemplateRoutes from 'client/Commands/CommandTemplateRoutes';
import CommandsRoutes from 'client/Commands/CommandsRoutes';
import ConfigurationGroupsRoutes from 'client/groups/Configuration/ConfigurationGroupsRoutes';
import ConfigurationsRoutes from 'client/policies/ConfigurationsRoutes';
import DeviceGroupRoutes from 'client/groups/System/DeviceGroupsRoutes';
import DevicesRoutes from 'client/systems/DevicesRoutes';
import DiscoverRoutes from 'client/Discover/DiscoverRoutes';
import GoogleAppDetailsRoutes from 'client/SoftwareApps/GoogleAppDetailsRoutes';
import HRDirectoryRoutes from 'client/directories/Workday/HRDirectoryRoutes';
import HomePageV2Routes from 'client/HomePageV2/HomePageV2Routes';
import InsightsRoutes from 'client/Insights/InsightsRoutes';
import LdapRoutes from 'client/directories/Ldap/LdapRoutes';
import LogoutRoutes from 'client/AdminPortal/router/LogoutRoutes';
import MdmIosRoutes from 'client/MDM/MdmIosRoutes';
import MdmMacRoutes from 'client/MDM/MdmMacRoutes';
import MdmRoutes from 'client/MDM/MdmRoutes';
import MspState from 'client/components/models/MspState';
import NotificationsRoutes from 'client/Notifications/NotificationsRoutes';
import OnboardingRoutes from 'client/GrowthEngineering/OnboardingRoutes';
import OsManagementRoutes from 'client/OsManagement/OsManagementRoutes';
import PasswordManagerRoutes from 'client/PasswordManager/PasswordManagerRoutes';
import PkiRoutes from 'client/Pki/PkiRoutes';
import PolicyGroupTemplateRoutes from 'client/groups/Configuration/PolicyGroupTemplateRoutes';
import PricingRoutes from 'client/Pricing/PricingRoutes';
import RadiusRoutes from 'client/Radius/RadiusRoutes';
import ReportsRoutes from 'client/Reports/ReportsRoutes';
import SecurityRoutes from 'client/Security/SecurityRoutes';
import SettingsRoutes from 'client/settings/SettingsRoutes';
import SoftwareAppsRoutes from 'client/SoftwareApps/SoftwareAppsRoutes';
import StickerRoutes from 'client/StickerPage/StickerRoutes';
import SupportRoutes from 'client/Support/SupportRoutes';
import TabbedAccountRoutes from 'client/Account/TabbedAccountRoutes';
import UserGroupRoutes from 'client/groups/User/UserGroupsRoutes';
import UsersRoutes from 'client/systemusers/Users/UsersRoutes';
import ValueReportRoutes from 'client/ValueReport/ValueReportRoutes';
import VueRouter from 'vue-router';
import store from 'client/store';

// Route Guards
import { addRootClasses } from '@jumpcloud/ui-components';
import accountPageGuard from 'client/AdminPortal/router/guards/accountPageGuard';
import cloudDirectoryGuard from 'client/AdminPortal/router/guards/cloudDirectoryGuard';
import commandRunnerGuard from 'client/AdminPortal/router/guards/commandRunnerGuard';
import dualRouterSwitchGuard from 'client/AdminPortal/router/guards/dualRouterSwitchGuard';
import eventTracking from 'client/AdminPortal/router/guards/eventTracking';
import manageCommandTemplatesGuard from 'client/AdminPortal/router/guards/manageCommandTemplatesGuard';
import managePolicyGroupTemplateGuard from 'client/AdminPortal/router/guards/managePolicyGroupTemplateGuard';
import onboardingRouteGuard from 'client/GrowthEngineering/OnboardingRouteGuard';
import policyGroupTemplateGuard from 'client/AdminPortal/router/guards/policyGroupTemplateGuard';
import pricingPageGuard from 'client/AdminPortal/router/guards/pricingPageGuard';
import removeFocus from 'client/AdminPortal/router/guards/removeFocus';
import trimHashGuard from 'client/AdminPortal/router/guards/trimHashGuard';
import valueReportPageGuard from 'client/ValueReport/valueReportPageGuard';

export const RouterOptions = {
  routes: [
    {
      path: '/',
      redirect: () => {
        const isCustomer = store.getters['SettingsModule/getIsCustomer'];
        const redirectPath = !isCustomer ? { path: '/discover' } : { path: '/home' };
        const showOnboarding = store.getters['OnboardingModule/getShowOnboarding'];

        if (showOnboarding) {
          redirectPath.path = '/onboarding';
        }

        return redirectPath;
      },
    },
    {
      path: '/dashboard',
      redirect: '/home',
      beforeEnter: (to, _, next) => {
        const orgId = to.query['jc-orgId'];

        if (!orgId) next(false);
        if (!MspState.isAnMsp) next(false);
        else next();
      },
    },
    ...ActiveDirectoryRoutes,
    ...AuthNPoliciesRoutes,
    ...CloudDirectoryRoutes,
    ...CommandsRoutes,
    ...ConfigurationGroupsRoutes,
    ...ConfigurationsRoutes,
    ...DeviceGroupRoutes,
    ...DevicesRoutes,
    ...DiscoverRoutes,
    ...GoogleAppDetailsRoutes,
    ...HomePageV2Routes,
    ...HRDirectoryRoutes,
    ...InsightsRoutes,
    ...LdapRoutes,
    ...LogoutRoutes,
    ...MdmIosRoutes,
    ...MdmMacRoutes,
    ...NotificationsRoutes,
    ...OnboardingRoutes,
    ...RadiusRoutes,
    ...StickerRoutes,
    ...SecurityRoutes,
    ...SettingsRoutes,
    ...SoftwareAppsRoutes,
    ...SupportRoutes,
    ...UserGroupRoutes,
    ...UsersRoutes,
    ...PricingRoutes,
  ],
};

// The store should be initialized before creating the Router
export function createVueRouter(_store = store) {
  const featureFlags = _store.getters['FeatureFlagsModule/betaFeatures'];

  /**
   * Flag to handle path between old router and new router
   * @todo Remove flags once the paths have been tested for some time on prod
   * @see https://jumpcloud.atlassian.net/browse/AUTH-7492
   */
  const {
    osManagement,
    pkiConsole,
  } = featureFlags;

  const RoutesRequiringFeatureFlags = [
    ...ApplicationRoutes(featureFlags),
    ...CloudInsightsRoutes(featureFlags),
    ...CommandTemplateRoutes(featureFlags),
    ...PolicyGroupTemplateRoutes(featureFlags),
    ...ReportsRoutes(featureFlags),
    ...ValueReportRoutes(featureFlags),
    ...TabbedAccountRoutes(featureFlags),
    ...PasswordManagerRoutes(featureFlags),
    ...MdmRoutes(featureFlags),
  ];

  const options = {
    ...RouterOptions,
    routes: [
      ...RouterOptions.routes,
      ...RoutesRequiringFeatureFlags,
    ],
  };

  if (osManagement) {
    options.routes = [
      ...options.routes,
      ...OsManagementRoutes,
    ];
  }

  if (pkiConsole) {
    options.routes = [
      ...options.routes,
      ...PkiRoutes,
    ];
  }

  return new VueRouter(options);
}

// These need to be run after globalRouteService has been initialized
export function initializeVueRouteGuards(vueRouter) {
  // dualRouterSwitchGuard should always be loaded first
  vueRouter.beforeEach(dualRouterSwitchGuard);
  vueRouter.beforeEach(trimHashGuard);
  vueRouter.beforeEach(commandRunnerGuard);
  vueRouter.beforeEach(accountPageGuard);
  vueRouter.beforeEach(policyGroupTemplateGuard);
  vueRouter.beforeEach(pricingPageGuard);
  vueRouter.beforeEach(manageCommandTemplatesGuard);
  vueRouter.beforeEach(managePolicyGroupTemplateGuard);
  vueRouter.beforeEach(valueReportPageGuard);
  vueRouter.beforeEach(cloudDirectoryGuard);
  vueRouter.beforeEach(onboardingRouteGuard);

  // Segment Event page view
  vueRouter.afterEach(eventTracking);
  vueRouter.afterEach(addRootClasses);
  vueRouter.afterEach(removeFocus);
}
